import React, { useState } from "react";
import { Box, Container, Grid, Typography, TextField, Button } from "@mui/material";
import AnimatedSection from "../common/AnimatedSection";
import SectionHeading from "../common/SectionHeading";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";

const NAVY = "#1B1F3B"; const CORAL = "#FF6B6B"; const YELLOW = "#FFD93D"; const TEAL = "#06D6A0";

const INFO = [
  { icon: <PhoneRoundedIcon />, label: "Call Us", color: TEAL },
  { icon: <EmailRoundedIcon />, label: "Email Us", color: CORAL },
  { icon: <LocationOnRoundedIcon />, label: "Visit Us", color: YELLOW },
];

const ContactSection = ({ schoolInfo, quickFacts }) => {
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => { e.preventDefault(); setSent(true); };

  const contactValues = [schoolInfo.phone, schoolInfo.email, schoolInfo.addressLines.slice(0, 2).join(", ")];

  return (
    <AnimatedSection id="contact" sx={{ backgroundColor: "#FFF8F0", py: { xs: 9, md: 12 } }}>
      <Container maxWidth="xl">
        <SectionHeading
          eyebrow="Contact Us"
          title="We’re Here to Help 💬"
          description="Have a question about admissions or the school? Get in touch with us and our team will be happy to help."
          eyebrowColor={TEAL}
          eyebrowBg="#E6FFF7"
        />

        <Grid container spacing={5}>
          {/* Contact info */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mb: 4 }}>
              {INFO.map((info, i) => (
                <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "flex-start", p: 2.5, borderRadius: "14px", border: `2.5px solid ${NAVY}`, backgroundColor: "#fff", boxShadow: `4px 4px 0 ${NAVY}` }}>
                  <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: "12px", backgroundColor: info.color, border: `2px solid ${NAVY}`, display: "grid", placeItems: "center", color: NAVY }}>
                    {info.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, color: NAVY, fontSize: "0.85rem", mb: 0.3 }}>{info.label}</Typography>
                    <Typography sx={{ color: "#4A4E6D", fontWeight: 600, fontSize: "0.9rem", lineHeight: 1.6 }}>{contactValues[i]}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Quick facts */}
            <Box sx={{ p: 3, borderRadius: "16px", border: `2.5px solid ${NAVY}`, backgroundColor: YELLOW, boxShadow: `5px 5px 0 ${NAVY}` }}>
              <Typography sx={{ fontFamily: "'Fredoka One',cursive", fontSize: "1.1rem", color: NAVY, mb: 2 }}>📋 Quick Facts</Typography>
              {quickFacts.map((f) => (
                <Box key={f} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: CORAL, border: `1.5px solid ${NAVY}`, flexShrink: 0 }} />
                  <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "0.9rem" }}>{f}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Contact form */}
          <Grid item xs={12} md={7}>
            <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: "20px", border: `2.5px solid ${NAVY}`, backgroundColor: "#fff", boxShadow: `6px 6px 0 ${NAVY}` }}>
              {sent ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography sx={{ fontSize: "4rem", mb: 2, animation: "bounce 1s ease-in-out infinite" }}>🎉</Typography>
                  <Typography sx={{ fontFamily: "'Fredoka One',cursive", fontSize: "1.8rem", color: TEAL, mb: 1 }}>Message Sent!</Typography>
                  <Typography sx={{ fontWeight: 600, color: "#4A4E6D" }}>We'll get back to you within 24 hours.</Typography>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleSubmit}>
                  <Typography sx={{ fontFamily: "'Fredoka One',cursive", fontSize: "1.4rem", color: NAVY, mb: 3 }}>Send Us A Message ✉️</Typography>
                  <Grid container spacing={2.5}>
                    {[{ label: "Parent Name", id: "name" }, { label: "Child's Name", id: "child" }, { label: "Phone Number", id: "phone" }, { label: "Email Address", id: "email", type: "email" }].map(f => (
                      <Grid item xs={12} sm={6} key={f.id}>
                        <TextField fullWidth required label={f.label} id={f.id} type={f.type || "text"}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", fontFamily: "'Nunito',sans-serif", fontWeight: 700, "& fieldset": { borderColor: NAVY, borderWidth: "2px" }, "&:hover fieldset": { borderColor: CORAL }, "&.Mui-focused fieldset": { borderColor: TEAL, borderWidth: "2.5px" } }, "& .MuiInputLabel-root": { fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, color: "#4A4E6D", "&.Mui-focused": { color: TEAL } } }}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <TextField fullWidth label="Class Interested In" select SelectProps={{ native: true }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", "& fieldset": { borderColor: NAVY, borderWidth: "2px" }, "&.Mui-focused fieldset": { borderColor: TEAL } }, "& .MuiInputLabel-root": { fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700 } }}>
                        {["Nursery", "LKG", "UKG", "Class 1–5", "Class 6–8", "Class 9–10", "Class 11–12"].map(c => <option key={c} value={c}>{c}</option>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={3} label="Your Message (Optional)"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", "& fieldset": { borderColor: NAVY, borderWidth: "2px" }, "&.Mui-focused fieldset": { borderColor: TEAL } }, "& .MuiInputLabel-root": { fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button type="submit" fullWidth size="large"
                        sx={{ py: 1.6, fontSize: "1rem", backgroundColor: CORAL, color: "#fff", border: `2.5px solid ${NAVY}`, boxShadow: `4px 4px 0 ${NAVY}`, borderRadius: "12px", fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, "&:hover": { backgroundColor: "#FF8080", transform: "translate(-2px,-2px)", boxShadow: `6px 6px 0 ${NAVY}` } }}>
                        Send Message 🚀
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AnimatedSection>
  );
};

export default ContactSection;
